import type { ComponentProps } from 'react';
import React from 'react';

import type { Breakpoint } from '../../utilities/useViewport';
import type {
  ShellContentProps,
  ShellFooterProps,
  ShellHeaderProps,
  ShellProps,
} from '../Shell/Shell';
import { styled } from '../../stitches.config';
import { useViewport } from '../../utilities/useViewport';
import { drawerWidth } from '../Drawer/Drawer';
import { Shell, ShellContent, ShellFooter, ShellHeader } from '../Shell/Shell';

const PaneHeaderContainer = styled(ShellHeader, {
  variants: {
    drawerAware: {
      true: {
        paddingRight: `$${drawerWidth}`,
      },
      false: {},
    },
  },
});

export type PaneHeaderProps = ShellHeaderProps & {
  /**
   * If true, the header will be aware of the drawer and will adjust its padding accordingly.
   */
  drawerAware?: boolean;
};

export function PaneHeader({ drawerAware = false, ...remaining }: PaneHeaderProps) {
  return <PaneHeaderContainer drawerAware={drawerAware} {...remaining} />;
}

const PaneContentContainer = styled(ShellContent, {
  variants: {
    drawerAware: {
      true: {
        paddingRight: `$${drawerWidth}`,
      },
      false: {},
    },
  },
});

export type PaneContentProps = ShellContentProps & {
  /**
   * If true, the content will be aware of the drawer and will adjust its padding accordingly.
   */
  drawerAware?: boolean;
};

export function PaneContent({
  children,
  drawerAware = false,
  gutter = 'none',
  spacing = 16,
  ref,
  ...remaining
}: PaneContentProps) {
  return (
    <PaneContentContainer
      gutter={gutter}
      spacing={spacing}
      drawerAware={drawerAware}
      ref={ref}
      {...remaining}
    >
      {children}
    </PaneContentContainer>
  );
}

const PaneFooterContainer = styled(ShellFooter, {
  variants: {
    drawerAware: {
      true: {
        paddingRight: `$${drawerWidth}`,
      },
      false: {},
    },
  },
});

export type PaneFooterProps = ShellFooterProps & {
  /**
   * Provide primary actions for your drawer.
   */
  actions?: React.ReactNode;
  /**
   * Fallback to `children` if `actions` or `secondaryActions` are not provided.
   */
  children?: React.ReactNode;
  /**
   * If true, the footer will be aware of the drawer and will adjust its padding accordingly.
   */
  drawerAware?: boolean;
  /**
   * Provide secondary actions, positioned at the start of the footer.
   */
  secondaryActions?: React.ReactNode;
} & ComponentProps<typeof PaneFooterContainer>;

export function PaneFooter({
  actions,
  children,
  secondaryActions,
  drawerAware = false,
  ...remaining
}: PaneFooterProps) {
  return (
    <PaneFooterContainer
      drawerAware={drawerAware}
      start={secondaryActions}
      end={actions || children}
      {...remaining}
    />
  );
}

export const PaneContainer = styled(Shell, {
  gridArea: 'pane',
  width: '100%',

  '@mobile': {
    widthAll: '100vw',
  },

  variants: {
    preset: {
      full: {},
      intermediate: {
        '@mobile': {
          height: '100%',
        },

        '@notMobile': {
          paddingTop: '$32',
        },
      },
      narrow: {
        '@mobile': {
          height: '100%',
        },

        '@notMobile': {
          paddingTop: '$32',
        },
      },
    },
  },
});

export type ShellPresetProp = 'narrow' | 'intermediate' | 'full';

export type PaneProps = ShellProps & {
  preset?: ShellPresetProp;
} & ComponentProps<typeof PaneContainer>;

const getPaneMaxWidth = (preset: ShellPresetProp, breakpoint: Breakpoint) => {
  switch (preset) {
    case 'intermediate':
      return breakpoint !== 'mobile' ? 1024 : undefined;
    case 'narrow':
      return breakpoint !== 'mobile' ? 760 : undefined;
    default:
      return 'full';
  }
};

export function Pane({ children, preset = 'full', ...remaining }: PaneProps) {
  const { breakpoint } = useViewport();
  return (
    <PaneContainer
      preset={preset}
      maxWidth={getPaneMaxWidth(preset, breakpoint)}
      widthMode={preset === 'full' ? 'full' : 'centered'}
      {...remaining}
    >
      {children}
    </PaneContainer>
  );
}
